import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Calculator, TrendingUp, TrendingDown, AlertTriangle, 
  DollarSign, Percent, RefreshCw, ChevronRight, ChevronDown,
  ArrowUpRight, ArrowDownRight, Minus
} from 'lucide-react';
import { formatCurrency } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function WhatIfScenario({ selectedContracts = [], onClose }) {
  const [rateChange, setRateChange] = useState(0);
  const [termChange, setTermChange] = useState(0);
  const [applyMarketAdjustment, setApplyMarketAdjustment] = useState(false);
  const [expandedContracts, setExpandedContracts] = useState({});

  const scenarioMutation = useMutation({
    mutationFn: async (params) => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.post(`${BACKEND_URL}/api/scenarios/what-if`, params, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
  });

  const runScenario = () => {
    scenarioMutation.mutate({
      contract_ids: selectedContracts,
      rate_change_percent: rateChange,
      term_change_months: termChange,
      apply_market_adjustment: applyMarketAdjustment
    });
  };

  const toggleContract = (contractId) => {
    setExpandedContracts(prev => ({
      ...prev,
      [contractId]: !prev[contractId]
    }));
  };

  const getDeltaIcon = (delta) => {
    if (delta > 0) return <ArrowUpRight className="w-3 h-3 text-green-600" />;
    if (delta < 0) return <ArrowDownRight className="w-3 h-3 text-red-600" />;
    return <Minus className="w-3 h-3 text-gray-400" />;
  };

  const getDeltaColor = (delta, inverse = false) => {
    if (inverse) {
      if (delta > 0) return 'text-red-600';
      if (delta < 0) return 'text-green-600';
    } else {
      if (delta > 0) return 'text-green-600';
      if (delta < 0) return 'text-red-600';
    }
    return 'text-gray-500';
  };

  return (
    <Card className="w-full" data-testid="what-if-scenario">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calculator className="w-5 h-5 text-[#09409C]" />
              What-If Scenario Modeling
            </CardTitle>
            <CardDescription>
              Model pricing changes and see projected impact on your portfolio
            </CardDescription>
          </div>
          {onClose && (
            <Button variant="ghost" size="sm" onClick={onClose}>✕</Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Scenario Parameters */}
        <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-medium text-sm">Scenario Parameters</h4>
          
          {/* Rate Change Slider */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Rate Change</Label>
              <Badge variant={rateChange > 0 ? 'destructive' : rateChange < 0 ? 'default' : 'outline'}>
                {rateChange > 0 ? '+' : ''}{rateChange}%
              </Badge>
            </div>
            <Slider
              value={[rateChange]}
              onValueChange={(v) => setRateChange(v[0])}
              min={-15}
              max={15}
              step={1}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>-15% (Decrease)</span>
              <span>+15% (Increase)</span>
            </div>
          </div>

          {/* Market Adjustment Toggle */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-sm">Apply Market Adjustment</Label>
              <p className="text-xs text-muted-foreground">
                Adjust scores based on current market rates
              </p>
            </div>
            <Switch
              checked={applyMarketAdjustment}
              onCheckedChange={setApplyMarketAdjustment}
            />
          </div>

          {/* Selected Contracts Count */}
          <div className="flex items-center justify-between pt-2 border-t">
            <span className="text-sm text-muted-foreground">
              Contracts to analyze:
            </span>
            <Badge variant="secondary">{selectedContracts.length}</Badge>
          </div>

          {/* Run Button */}
          <Button 
            className="w-full bg-[#09409C] hover:bg-[#146CBB]"
            onClick={runScenario}
            disabled={selectedContracts.length === 0 || scenarioMutation.isPending}
          >
            {scenarioMutation.isPending ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Calculating...
              </>
            ) : (
              <>
                <Calculator className="w-4 h-4 mr-2" />
                Run Scenario Analysis
              </>
            )}
          </Button>
        </div>

        {/* Results */}
        {scenarioMutation.data && (
          <div className="space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 gap-3">
              <Card className="border-green-200">
                <CardContent className="pt-4 pb-3">
                  <div className="text-xs text-muted-foreground mb-1">Current Value</div>
                  <div className="text-lg font-bold">
                    {formatCurrency(scenarioMutation.data.summary.currentTotalValue)}
                  </div>
                </CardContent>
              </Card>
              <Card className={scenarioMutation.data.summary.valueDelta >= 0 ? 'border-green-200' : 'border-red-200'}>
                <CardContent className="pt-4 pb-3">
                  <div className="text-xs text-muted-foreground mb-1">Projected Value</div>
                  <div className="text-lg font-bold flex items-center gap-2">
                    {formatCurrency(scenarioMutation.data.summary.projectedTotalValue)}
                    {getDeltaIcon(scenarioMutation.data.summary.valueDelta)}
                  </div>
                  <div className={`text-xs ${getDeltaColor(scenarioMutation.data.summary.valueDelta)}`}>
                    {scenarioMutation.data.summary.valueDelta >= 0 ? '+' : ''}
                    {formatCurrency(scenarioMutation.data.summary.valueDelta)}
                  </div>
                </CardContent>
              </Card>
              <Card className="border-orange-200">
                <CardContent className="pt-4 pb-3">
                  <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Current At-Risk
                  </div>
                  <div className="text-lg font-bold text-orange-600">
                    {formatCurrency(scenarioMutation.data.summary.currentAtRiskValue)}
                  </div>
                </CardContent>
              </Card>
              <Card className={scenarioMutation.data.summary.projectedAtRiskValue <= scenarioMutation.data.summary.currentAtRiskValue ? 'border-green-200' : 'border-red-200'}>
                <CardContent className="pt-4 pb-3">
                  <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Projected At-Risk
                  </div>
                  <div className="text-lg font-bold">
                    {formatCurrency(scenarioMutation.data.summary.projectedAtRiskValue)}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contract Details */}
            <div>
              <h4 className="font-medium text-sm mb-2">Contract Impact Details</h4>
              <ScrollArea className="h-[300px] rounded-md border">
                <div className="p-2 space-y-2">
                  {scenarioMutation.data.contracts.map((contract) => (
                    <div 
                      key={contract.contractId}
                      className="border rounded-lg overflow-hidden"
                    >
                      <div 
                        className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer hover:bg-gray-100"
                        onClick={() => toggleContract(contract.contractId)}
                      >
                        <div>
                          <div className="font-medium text-sm">{contract.accountName}</div>
                          <div className="text-xs text-muted-foreground">{contract.contractNumber}</div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className={`text-sm font-medium ${getDeltaColor(contract.delta.valuePercent)}`}>
                            {contract.delta.valuePercent > 0 ? '+' : ''}{contract.delta.valuePercent}%
                          </div>
                          {expandedContracts[contract.contractId] ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </div>
                      </div>
                      
                      {expandedContracts[contract.contractId] && (
                        <div className="p-3 space-y-2 text-sm">
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <div className="text-xs text-muted-foreground">Metric</div>
                            </div>
                            <div className="text-center">
                              <div className="text-xs text-muted-foreground">Current</div>
                            </div>
                            <div className="text-center">
                              <div className="text-xs text-muted-foreground">Projected</div>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-4 border-t pt-2">
                            <div className="text-xs font-medium">Value</div>
                            <div className="text-center text-xs">{formatCurrency(contract.current.value)}</div>
                            <div className={`text-center text-xs font-medium ${getDeltaColor(contract.delta.value)}`}>
                              {formatCurrency(contract.projected.value)}
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-4">
                            <div className="text-xs font-medium">Rate</div>
                            <div className="text-center text-xs">${contract.current.rate.toFixed(4)}</div>
                            <div className={`text-center text-xs font-medium ${getDeltaColor(contract.projected.rate - contract.current.rate, true)}`}>
                              ${contract.projected.rate.toFixed(4)}
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-4">
                            <div className="text-xs font-medium">Competitiveness</div>
                            <div className="text-center">
                              <Badge variant={contract.current.competitivenessScore >= 70 ? 'default' : contract.current.competitivenessScore >= 50 ? 'secondary' : 'destructive'} className="text-[10px]">
                                {contract.current.competitivenessScore}%
                              </Badge>
                            </div>
                            <div className="text-center">
                              <Badge variant={contract.projected.competitivenessScore >= 70 ? 'default' : contract.projected.competitivenessScore >= 50 ? 'secondary' : 'destructive'} className="text-[10px]">
                                {contract.projected.competitivenessScore}%
                              </Badge>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          </div>
        )}

        {/* Empty State */}
        {selectedContracts.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <Calculator className="w-12 h-12 mx-auto mb-3 opacity-20" />
            <p className="text-sm">Select contracts on the map to run scenario analysis</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
